/**
 * As duas vistas do diretório: quem está no grupo, e em que grupos está a
 * pessoa.
 *
 * ## Por que os dois moram no mesmo arquivo
 *
 * São a mesma pergunta pelos dois lados, e — o que de fato os une — **falham
 * igual**. Os dois leem o Graph ao vivo, os dois podem voltar sem resposta por
 * permissão, sessão ou rede, e os dois têm de dizer isso sem fingir que a lista
 * vazia é a verdade. Esse vocabulário de falha está escrito uma vez, em
 * `AvisoDoDiretorio`, e é o que justifica a vizinhança.
 *
 * ## O que estas telas mostram que nenhuma das duas fontes sabe sozinha
 *
 * O Entra sabe quem está no grupo. O banco sabe quem já entrou no AI Hub.
 * Quem administra precisa das duas ao mesmo tempo: *estas pessoas ganham o
 * agente, e destas, estas já estão usando a plataforma.* É esse cruzamento que
 * o servidor monta e que aqui vira o selo de cada linha.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, photoUrl, SessionError, goToSignIn } from '../api.js';
import { initialsOf } from './AppShell.jsx';
import { Avatar } from './Avatar.jsx';

/**
 * Por que a lista não veio, em português e sem culpar quem está lendo.
 *
 * `sem_token` é o único que pede ação de quem vê (entrar de novo). Os outros
 * são de configuração ou de rede, e dizer "tente novamente" neles seria mandar
 * a pessoa repetir uma coisa que não vai mudar.
 */
const MOTIVOS = {
  sem_token: 'Sua sessão com o Entra expirou. Saia e entre de novo para consultar o diretório.',
  recusado:
    'O Entra recusou a consulta. A permissão de leitura do diretório pode ter sido revogada.',
  erro: 'Não consegui falar com o Entra agora.',
};

/** O 404 do diretório tem leitura própria, e é útil. */
const SUMIU_DO_DIRETORIO =
  'Esta conta não existe mais no diretório. Costuma ser alguém que saiu da empresa e continua com o login gravado aqui.';

function AvisoDoDiretorio({ motivo, status, completa, cortadaEm }) {
  if (motivo === 'ok') {
    if (completa) return null;
    return (
      <p className="state-message" role="status">
        A lista é longa e foi cortada em {cortadaEm}. O grupo tem mais gente do que cabe aqui.
      </p>
    );
  }

  const texto = motivo === 'recusado' && status === 404 ? SUMIU_DO_DIRETORIO : MOTIVOS[motivo];

  return (
    <p className="state-message error" role="alert">
      {texto ?? 'Não consegui consultar o diretório.'}
    </p>
  );
}

/** Carrega um endereço quando o painel abre, e cuida dos três estados. */
function useDoDiretorio(caminho) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setErro(null);
    setDados(null);
    try {
      setDados(await apiFetch(caminho));
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setErro(err.message);
    }
    return undefined;
  }, [caminho]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return { dados, erro, recarregar: carregar };
}

/** A moldura, igual para os dois painéis. */
function Painel({ titulo, subtitulo, onClose, children }) {
  useEffect(() => {
    const aoTeclar = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel grupo-painel"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <div>
            <h2 className="panel-title">{titulo}</h2>
            {subtitulo && <p className="panel-desc">{subtitulo}</p>}
          </div>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>
        <div className="panel-body editor-body">{children}</div>
      </div>
    </div>
  );
}

/** Normaliza para busca: sem acento e em minúsculas. */
const paraBusca = (texto) => (texto ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/* ── quem está no grupo ──────────────────────────────────────────────────── */

/**
 * As pessoas de um grupo do Entra, com quem já entrou no AI Hub marcado.
 *
 * ## Foto só para quem já entrou, e isso não é limitação da tela
 *
 * A foto é lida do `/me` da própria pessoa, no login dela. Para quem nunca
 * entrou não existe foto nenhuma guardada aqui, e buscá-la de terceiros no
 * Graph exigiria `ProfilePhoto.Read.All`, que não foi pedida ao IAM. As
 * iniciais não são um erro de carregamento: são o estado certo.
 */
export function PessoasDoGrupo({ grupo, onClose }) {
  const { dados, erro, recarregar } = useDoDiretorio(
    `/api/admin/grupos/${encodeURIComponent(grupo.id)}/pessoas`,
  );
  const [busca, setBusca] = useState('');

  const pessoas = useMemo(() => dados?.pessoas ?? [], [dados]);
  const jaEntraram = pessoas.filter((p) => p.jaEntrou).length;

  const visiveis = useMemo(() => {
    const termo = paraBusca(busca.trim());
    if (!termo) return pessoas;
    return pessoas.filter(
      (p) => paraBusca(p.name).includes(termo) || paraBusca(p.upn).includes(termo),
    );
  }, [pessoas, busca]);

  return (
    <Painel
      titulo={grupo.displayName ?? 'Grupo sem nome'}
      subtitulo={<span className="grupo-id">{grupo.id}</span>}
      onClose={onClose}
    >
      {!dados && !erro && <p className="state-message">Consultando o diretório…</p>}

      {erro && (
        <div className="state-message error" role="alert">
          <p>{erro}</p>
          <button type="button" onClick={recarregar}>
            Tentar de novo
          </button>
        </div>
      )}

      {dados && (
        <>
          <p className="grupo-alcance-linha">
            {pessoas.length === 0
              ? 'Nenhuma pessoa neste grupo.'
              : `${pessoas.length} ${pessoas.length === 1 ? 'pessoa' : 'pessoas'} no Entra — ${jaEntraram} já ${jaEntraram === 1 ? 'entrou' : 'entraram'} no AI Hub.`}
            {dados.gruposDentro > 0 && (
              // Explica a lista ter gente que ninguém pôs ali à mão.
              <>
                {' '}
                Contém {dados.gruposDentro}{' '}
                {dados.gruposDentro === 1 ? 'grupo aninhado' : 'grupos aninhados'}, e as pessoas
                deles estão incluídas.
              </>
            )}
          </p>

          <AvisoDoDiretorio
            motivo={dados.motivo}
            status={dados.status}
            completa={dados.completa}
            cortadaEm={pessoas.length}
          />

          {pessoas.length > 8 && (
            <input
              className="search-box"
              type="search"
              placeholder="Buscar pessoa…"
              aria-label="Buscar pessoa no grupo"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          )}

          {visiveis.length === 0 && pessoas.length > 0 && (
            <p className="state-message">Ninguém corresponde ao que você procurou.</p>
          )}

          <ul className="diretorio-lista">
            {visiveis.map((p) => (
              <li key={p.oid} className="diretorio-linha">
                <Avatar
                  src={p.hasPhoto ? photoUrl.usuario(p.oid) : null}
                  initials={initialsOf(p.name, p.upn)}
                  name={p.name ?? p.upn}
                  size="sm"
                />
                <span className="person-text">
                  <span className="person-name">{p.name ?? '—'}</span>
                  <span className="person-upn">{p.upn ?? p.oid}</span>
                </span>
                <span className={`status-pill ${p.jaEntrou ? 'ok' : 'neutral'}`}>
                  {p.jaEntrou ? 'No AI Hub' : 'Ainda não entrou'}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Painel>
  );
}

/* ── em que grupos a pessoa está ─────────────────────────────────────────── */

/**
 * Os grupos de uma pessoa, e o que cada um libera.
 *
 * ## Por que lista também os grupos que não liberam nada
 *
 * Quem abre isto está respondendo *por que fulano (não) consegue abrir X*.
 * Mostrar só os grupos que a plataforma usa esconderia a metade da resposta que
 * costuma ser a certa: **o grupo que falta**. Vendo os dezenas de grupos que a
 * pessoa tem e nenhum deles sendo o que libera o item, a conclusão é imediata.
 */
export function GruposDaPessoa({ usuario, onClose }) {
  const { dados, erro, recarregar } = useDoDiretorio(
    `/api/admin/users/${encodeURIComponent(usuario.oid)}/grupos`,
  );
  const [busca, setBusca] = useState('');

  const todos = useMemo(() => dados?.grupos ?? [], [dados]);
  const liberam = todos.filter((g) => g.itens.length > 0);

  const visiveis = useMemo(() => {
    const termo = paraBusca(busca.trim());
    if (!termo) return todos;
    return todos.filter((g) => paraBusca(g.displayName ?? g.id).includes(termo));
  }, [todos, busca]);

  return (
    <Painel titulo={usuario.displayName ?? usuario.upn} subtitulo={usuario.upn} onClose={onClose}>
      {!dados && !erro && <p className="state-message">Consultando o diretório…</p>}

      {erro && (
        <div className="state-message error" role="alert">
          <p>{erro}</p>
          <button type="button" onClick={recarregar}>
            Tentar de novo
          </button>
        </div>
      )}

      {dados && (
        <>
          <p className="grupo-alcance-linha">
            {todos.length === 0
              ? 'Nenhum grupo encontrado no Entra.'
              : `${todos.length} ${todos.length === 1 ? 'grupo' : 'grupos'} no Entra — ${
                  liberam.length === 0
                    ? 'nenhum deles libera item do catálogo.'
                    : `${liberam.length} ${liberam.length === 1 ? 'libera' : 'liberam'} item do catálogo.`
                }`}
          </p>

          <AvisoDoDiretorio
            motivo={dados.motivo}
            status={dados.status}
            completa={dados.completa}
            cortadaEm={todos.length}
          />

          {todos.length > 8 && (
            <input
              className="search-box"
              type="search"
              placeholder="Buscar grupo…"
              aria-label="Buscar grupo da pessoa"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          )}

          {visiveis.length === 0 && todos.length > 0 && (
            <p className="state-message">Nenhum grupo corresponde ao que você procurou.</p>
          )}

          <ul className="diretorio-lista">
            {visiveis.map((g) => (
              <li key={g.id} className={`diretorio-linha ${g.itens.length ? '' : 'is-mudo'}`}>
                <span className="person-text">
                  <span className="person-name">{g.displayName ?? 'Grupo sem nome'}</span>
                  <span className="person-upn grupo-id">{g.id}</span>
                </span>
                {g.itens.length > 0 ? (
                  <span className="grupo-itens">
                    {g.itens.map((i) => (
                      <span key={i.id} className="grupo-chip">
                        {i.name}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="cell-sub">não libera nada aqui</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Painel>
  );
}

export default { PessoasDoGrupo, GruposDaPessoa };
